import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Download, ChevronDown, User, Layout, Shuffle, Brain, Server, Cpu, Database, X, Mail } from 'lucide-react';
import { portfolioData } from '../data/mock';

const nodeIcons = {
  user: User, layout: Layout, shuffle: Shuffle, brain: Brain, server: Server, cpu: Cpu, database: Database
};
const nodeColors = {
  users: 'from-indigo-500 to-purple-600', applications: 'from-blue-500 to-indigo-600',
  api: 'from-cyan-500 to-teal-600', ai: 'from-violet-500 to-purple-600',
  infra: 'from-emerald-500 to-green-600', clusters: 'from-amber-500 to-orange-600',
  data: 'from-rose-500 to-pink-600'
};

const Hero = () => {
  const [visible, setVisible] = useState(false);
  const [activeNode, setActiveNode] = useState(0);
  const [showResumeAlert, setShowResumeAlert] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    setVisible(true);
    const interval = setInterval(() => setActiveNode(prev => (prev + 1) % (portfolioData.heroArchitecture.length + 1)), 2000);
    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="hero" ref={sectionRef} className="relative min-h-screen flex items-center overflow-hidden pt-24 sm:pt-28">
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-[200px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className={`transition-all duration-1000 transform ${visible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-mono mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span>{portfolioData.personal.headline}</span>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start mb-12">
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-4">
                Building AI-Powered{' '}
                <span className="gradient-text">Infrastructure</span>
                <br />
                <span className="text-gray-300">& Intelligent Automation Systems</span>
              </h1>
              <p className="text-gray-500 text-base sm:text-lg leading-relaxed mb-8 max-w-xl">{portfolioData.personal.description}</p>

              <div className="flex flex-wrap gap-3">
                <button onClick={() => scrollToSection('projects')}
                  className="group relative px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium text-sm transition-all duration-300 hover:shadow-lg hover:shadow-indigo-500/25 hover:scale-105">
                  <span className="relative flex items-center space-x-2">
                    <span>Explore My Systems</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
                <button onClick={() => scrollToSection('timeline')}
                  className="px-6 py-3 glass text-gray-300 rounded-xl font-medium text-sm transition-all duration-300 hover:bg-white/10 hover:text-white hover:border-indigo-500/30">
                  View Engineering Journey
                </button>
                <a href="/resume.html" target="_blank" rel="noopener noreferrer"
                  className="px-6 py-3 glass text-gray-300 rounded-xl font-medium text-sm transition-all duration-300 hover:bg-white/10 hover:text-white hover:border-indigo-500/30 flex items-center space-x-2">
                  <Download size={16} /><span>Resume</span>
                </a>
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="glass rounded-3xl p-8 border border-white/5">
                <h3 className="text-gray-400 text-xs font-mono mb-6 text-center">Live System Architecture</h3>
                <div className="space-y-0">
                  {portfolioData.heroArchitecture.map((node, i) => {
                    const Icon = nodeIcons[node.icon] || Server;
                    const isActive = i === activeNode;
                    return (
                      <div key={node.id}>
                        <div className={`flex items-center space-x-4 p-3 rounded-xl transition-all duration-500 ${
                          isActive ? 'bg-indigo-500/10 border border-indigo-500/20' : 'bg-white/[0.02] border border-transparent'
                        }`}>
                          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${nodeColors[node.id]} flex items-center justify-center transition-all duration-500 ${isActive ? 'scale-110 shadow-lg shadow-indigo-500/25' : ''}`}>
                            <Icon className="text-white" size={18} />
                          </div>
                          <div className="flex-1">
                            <div className={`text-sm font-medium transition-all duration-500 ${isActive ? 'text-white' : 'text-gray-400'}`}>{node.label}</div>
                            <div className="text-gray-600 text-xs font-mono">{node.id}</div>
                          </div>
                          {isActive && <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse" />}
                        </div>
                        {i < portfolioData.heroArchitecture.length - 1 && (
                          <div className="flex justify-center py-1">
                            <div className={`w-0.5 h-4 rounded-full transition-all duration-500 ${i < activeNode ? 'bg-indigo-500/50' : 'bg-white/5'}`} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 max-w-5xl">
            {portfolioData.metricsDashboard.map((m) => (
              <div key={m.label} className="glass rounded-xl p-4 text-center hover:border-indigo-500/20 transition-all duration-300">
                <div className={`text-lg sm:text-xl font-bold bg-gradient-to-r ${m.color} bg-clip-text text-transparent font-mono`}>{m.value}</div>
                <div className="text-gray-600 text-[10px] sm:text-xs mt-1 leading-tight">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <button onClick={() => scrollToSection('brand')} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gray-600 hover:text-indigo-400 transition-colors animate-bounce">
        <ChevronDown size={24} />
      </button>

      {showResumeAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowResumeAlert(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative glass rounded-2xl p-8 max-w-md w-full border border-white/10 shadow-2xl shadow-indigo-500/10" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowResumeAlert(false)} className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors">
              <X size={18} />
            </button>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-5 shadow-lg shadow-amber-500/20">
              <Download size={22} className="text-white" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Resume Unavailable</h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              Resume download is being configured. Reach me at{' '}
              <a href="mailto:ranjithkumar98.sekar@gmail.com" className="text-indigo-400 hover:text-indigo-300 underline">
                ranjithkumar98.sekar@gmail.com
              </a>
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
